// src/hooks/useCrud.js
import { useState, useEffect } from "react";
import  apiService  from "../services/apiService";
import authService from "../services/authService";
import { useNavigate } from "react-router-dom";

export const useCrud = (endpoint, fileFields = []) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    if (!authService.isLoggedIn()) {
      setErrors({ auth: "User not authenticated" });
      setLoading(false);
      navigate("/login");
      return;
    }
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await apiService.getAll(endpoint);
      setData(res.data.data || res.data);
      setErrors({});
    } catch (error) {
      console.error("Fetch error:", error);
      setErrors({ fetch: error.message || "Failed to fetch data" });
      if (error.response?.status === 401) {
        authService.logout();
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchItem = async (id) => {
    try {
      const res = await apiService.getById(endpoint, id);
      setErrors({});
      return res.data.data || res.data;
    } catch (err) {
      console.error("Fetch item error:", err);
      setErrors({ general: err.message || "Failed to fetch item" });
      throw err;
    }
  };

  const hasFile = (item) => fileFields.some((field) => item[field] instanceof File);

  const createItem = async (item) => {
    try {
      const res = hasFile(item)
        ? await apiService.createWithFile(endpoint, item)
        : await apiService.create(endpoint, item);
      setErrors({});
      fetchAll();
      return res.data;
    } catch (err) {
      console.error("Create error:", err.response?.data);
      if (err.response?.status === 422) {
        setErrors(err.response.data.errors || { general: "Validation failed" });
      } else {
        setErrors({ general: err.message || "Create failed" });
      }
      throw err;
    }
  };

  const updateItem = async (id, item) => {
    try {
      const res = hasFile(item)
        ? await apiService.updateWithFile(endpoint, id, item)
        : await apiService.update(endpoint, id, item);
      setErrors({});
      fetchAll();
      return res.data;
    } catch (err) {
      console.error("Update error:", err.response?.data);
      if (err.response?.status === 422) {
        setErrors(err.response.data.errors || { general: "Validation failed" });
      } else {
        setErrors({ general: err.message || "Update failed" });
      }
      throw err;
    }
  };

  const deleteItem = async (id) => {
    try {
      await apiService.remove(endpoint, id);
      fetchAll();
    } catch (err) {
      console.error("Delete error:", err);
      setErrors({ general: err.message || "Delete failed" });
    }
  };

  return { data, setData, loading, errors, createItem, updateItem, deleteItem, fetchItem };

};